import React, { useState } from 'react';
import "../Styles/Faq.css";
import { Fade } from "react-awesome-reveal";
import { motion } from "framer-motion";
import { Slide, Zoom } from 'react-awesome-reveal';
import { Helmet } from "react-helmet-async";
import { FaQuestion } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const faqData = [
  {
    category: "General",
    icon: "🌍",
    questions: [
      "Is this really free or is it a scam?",
      "Do I need to be on benefits to qualify?",
      "What types of properties are eligible?",
      "Do the products come with warranties?",
      "Who will carry out the work?",
      "How long does installation take?",
      "Can I visit your office in person?",
      "Do you install windows?",
    ],
  },
  {
    category: "Solar",
    icon: "☀️",
    questions: [
      "How many solar panels will I receive?",
      "What is the wattage of the solar panels?",
      "What warranties do the solar panels have?",
      "Can I get a battery with my solar system?",
      "What roof types can you install panels on?",
      "Can I sell surplus energy back to the grid?",
      "Does this mean you own my roof?",
    ],
  },
  {
    category: "Air Source Heat Pumps",
    icon: "❄️",
    questions: [
      "How long is the warranty on a heat pump?",
      "How noisy are air source heat pumps?",
      "How hot will my radiators get with a heat pump?",
    ],
  },
  {
    category: "Insulation",
    icon: "🏡",
    questions: [
      "How does insulation reduce energy bills?",
      "How thick is the insulation you install?",
      "What type of cavity wall insulation do you use?",
      "What is the lifespan of insulation?",
      "How long does insulation take to install?",
      "Will I need to re-plaster my walls afterwards?",
    ],
  },
];


const faqAnswers = {
  // === General ===
  "Is this really free or is it a scam?":
    "Yes, it’s genuinely free. We deliver energy efficiency upgrades through government-backed ECO and GBIS schemes. These initiatives are designed to cut energy costs and reduce fuel poverty across the UK—no catch, no scam.",

  "Do I need to be on benefits to qualify?":
    "Not always. Aside from certain benefits, you can also qualify if your annual household income is £31,000 or less, or if someone in your household has a medical condition making them sensitive to cold. For GBIS grants, eligibility may also be based on your Council Tax band.",

  "What types of properties are eligible?":
    "Homes with an EPC rating of D or below usually qualify. If you’re unsure of your property’s EPC, we can check it for you.",

  "Do the products come with warranties?":
    "Absolutely. Every installation includes warranties ranging from 2 years up to 25 years depending on the product and manufacturer.",

  "Who will carry out the work?":
    "All work is handled by our in-house team—from your initial enquiry, through installation, to aftercare support.",

  "How long does installation take?":
    "It varies by upgrade. Some measures can be completed in a day, while others may take longer. We’ll always schedule the work around your availability and keep you updated throughout.",

  "Can I visit your office in person?":
    "Of course! Our offices are open weekdays 9–5 in Llanelli town centre. You’re welcome to visit us to discuss eligibility, installation, or any other queries.",

  "Do you install windows?":
    "No, we don’t provide window installation or replacement services.",

  // === Solar ===
  "How many solar panels will I receive?":
    "It depends on your property. After a survey, we’ll calculate the number of panels required to achieve the best possible EPC rating under the scheme.",

  "What is the wattage of the solar panels?":
    "Our solar panels generally operate between 410w and 435w per panel.",

  "What warranties do the solar panels have?":
    "The panels come with a 12-year product warranty and a 30-year performance guarantee.",

  "Can I get a battery with my solar system?":
    "Batteries aren’t covered under the government schemes, but we can provide you with a separate quote if you’d like one installed.",

  "What roof types can you install panels on?":
    "We can install solar panels on almost any roof type, except those made with asbestos tiles.",

  "Can I sell surplus energy back to the grid?":
    "Yes. Extra energy can be sold back to your supplier through the Smart Export Guarantee.",

  "Does this mean you own my roof?":
    "Not at all. This isn’t a rent-a-roof scheme—you own the system from day one.",

  // === Heat Pumps ===
  "How long is the warranty on a heat pump?":
    "Our air source heat pumps come with 5–7 year warranties depending on the manufacturer.",

  "How noisy are air source heat pumps?":
    "They run at around 40–60 decibels, similar to the sound of a household fridge, making them suitable for residential areas.",

  "How hot will my radiators get with a heat pump?":
    "Heat pump radiators typically operate at 35–55°C instead of the 70–80°C common with gas boilers. They provide a consistent, comfortable warmth throughout the day, which improves efficiency and reduces bills.",

  // === Insulation ===
  "How does insulation reduce energy bills?":
    "Insulation keeps heat from escaping, meaning your home stays warmer for longer and your heating system doesn’t have to work as hard. This reduces your energy consumption and lowers bills.",

  "How thick is the insulation you install?":
    "It depends on the type—typically 300mm for loft insulation, 100mm for internal wall insulation, and room-in-roof solutions.",

  "What type of cavity wall insulation do you use?":
    "We use bonded polyester beads. They’re breathable, durable, and won’t slump over time.",

  "What is the lifespan of insulation?":
    "Most of our insulation products last 25 years or more.",

  "How long does insulation take to install?":
    "Loft insulation can be done in a few hours, cavity wall in a day, while internal wall insulation may take up to a week depending on the property size.",

  "Will I need to re-plaster my walls afterwards?":
    "Yes, but we take care of this for you. Once installation is complete, we plaster over your walls so they’re ready to decorate.",
};


const FaqPage = () => {
  const text = "Frequently Asked Questions".split("");
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions || Paramount Green Services</title>
        <meta name="description" content="Free heating, insulation, and solar panel grants across the UK. Transform your home’s energy efficiency today." />
      </Helmet>

      <div className="faq-wrapper">
        {/* Header */}
        <section className="faq-header text-center">
          <div className="container">
            <div className="icon-circle mb-3 mt-4">
              <FaQuestion size={36} style={{ color: '#000000' }} />
            </div>
            <h1 className="mt-4">{text.map((char, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.025, type: "spring", stiffness: 500, damping: 30 }}
              >
                {char}
              </motion.span>
            ))}</h1>
            <p>
              <Fade>
                Find answers to common questions about our services. Can’t find what you're looking for?
                Contact our support team anytime.
              </Fade>
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <Fade>
          <section className="faq-section container my-5">
            {faqData.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-5">
                <h5 className="faq-category-title">
                  <span className="faq-icon">{group.icon}</span> {group.category}
                </h5>

                <div className="accordion" id={`accordion-${groupIndex}`}>
                  {group.questions.map((question, questionIndex) => {
                    const id = `q-${groupIndex}-${questionIndex}`;
                    const isOpen = openQuestion === id;

                    return (
                      <div className="accordion-item" key={id}>
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button custom-toggle d-flex justify-content-between align-items-center ${!isOpen ? 'collapsed' : ''}`}
                            type="button"
                            onClick={() => toggleQuestion(id)}
                          >
                            <span>{question}</span>
                            <span className="toggle-icon ms-auto">{isOpen ? '−' : '+'}</span>
                          </button>
                        </h2>
                        <div
                          className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}
                        >
                          <div className="accordion-body">
                            <p>{faqAnswers[question] || "Answer coming soon. Please contact our support team for more details."}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        </Fade>

        {/* Contact Footer */}
        <section className="faq-contact-footer text-center py-5">
          <div className="container">
            <h4 className="mb-3"><Fade>Still Have Questions?</Fade></h4>
            <p className="mb-4 text-muted">
              Our friendly customer support team is here to help you with any questions not covered in our FAQ section.
            </p>
            <Zoom>
              <div className="row justify-content-center">
                <div className="col-md-3 col-sm-6 mb-4">
                  <div className="contact-box">
                    <i className="fas fa-phone fa-2x mb-2"></i>
                    <p><strong>Call Us</strong></p>
                    <p> <a href='tel:(92) 306-8629918' style={{ textDecoration: "none", }}>(92) 306-8629918</a><br />Available 24/7</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-4">
                  <div className="contact-box">
                    <i className="fas fa-comments fa-2x mb-2"></i>
                    <p><strong>Live Chat</strong></p>
                    <p>Instant support<br />1PM – 10PM</p>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 mb-4">
                  <div className="contact-box">
                    <i className="fas fa-map-marker-alt fa-2x mb-2"></i>
                    <p><strong>Visit Us</strong></p>
                    <p>Lahore Office<br />Mon–Sat 9AM–6PM</p>
                  </div>
                </div>
              </div>
            </Zoom>

            <div className="mt-4">
              <button className="btn btn-outline-secondary btn-modern me-3">Contact Support</button>
              <button
                className="btn btn-primary btn-modern"
                onClick={() => window.location.href = '/Contactus'}
              >
                Send us a Message
              </button>
            </div>


          </div>
        </section>
      </div>
    </>
  );
};


export default FaqPage;
